import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { adminApi } from '../services/api';
import {
  Building2,
  DollarSign,
  Users,
  Target,
  TrendingUp,
  TrendingDown,
  ArrowRight,
  Clock,
  CheckCircle2,
  AlertCircle,
  BarChart3
} from 'lucide-react';

const StatCard = ({ title, value, change, changeType, icon: Icon, color, link }) => {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    purple: 'bg-purple-100 text-purple-600',
    orange: 'bg-orange-100 text-orange-600',
    red: 'bg-red-100 text-red-600'
  };

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between">
        <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
        {change && (
          <div
            className={`flex items-center text-sm ${
              changeType === 'increase' ? 'text-green-600' : 'text-red-600'
            }`}
          >
            {changeType === 'increase' ? (
              <TrendingUp className="w-4 h-4 mr-1" />
            ) : (
              <TrendingDown className="w-4 h-4 mr-1" />
            )}
            {change}
          </div>
        )}
      </div>
      <div className="mt-4">
        <h3 className="text-2xl font-bold text-gray-900">{value}</h3>
        <p className="text-sm text-gray-500 mt-1">{title}</p>
      </div>
      {link && (
        <Link
          to={link}
          className="mt-4 inline-flex items-center text-sm text-primary-600 hover:text-primary-700 font-medium"
        >
          View details
          <ArrowRight className="w-4 h-4 ml-1" />
        </Link>
      )}
    </div>
  );
};

const RecentActivity = ({ activities }) => {
  const getActivityIcon = (type) => {
    switch (type) {
      case 'tenant_created':
        return <Building2 className="w-4 h-4 text-blue-500" />;
      case 'payment_received':
        return <DollarSign className="w-4 h-4 text-green-500" />;
      case 'lead_converted':
        return <CheckCircle2 className="w-4 h-4 text-purple-500" />;
      case 'ticket_opened':
        return <AlertCircle className="w-4 h-4 text-orange-500" />;
      default:
        return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };

  return (
    <div className="card">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">Recent Activity</h3>
      </div>
      <div className="divide-y divide-gray-100">
        {activities.map((activity, index) => (
          <div key={index} className="px-6 py-4 flex items-start">
            <div className="flex-shrink-0 mt-1">
              {getActivityIcon(activity.type)}
            </div>
            <div className="ml-3 flex-1">
              <p className="text-sm text-gray-900">{activity.message}</p>
              <p className="text-xs text-gray-500 mt-1">{activity.time}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="px-6 py-4 border-t border-gray-200">
        <Link
          to="/settings/audit-logs"
          className="text-sm text-primary-600 hover:text-primary-700 font-medium"
        >
          View all activity
        </Link>
      </div>
    </div>
  );
};

const DashboardPage = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await adminApi.getDashboard();
      setStats(response.data.data);
    } catch (err) {
      setError('Failed to load dashboard data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
        <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
        <p className="text-sm text-red-700">{error}</p>
      </div>
    );
  }

  const recentActivities = [
    { type: 'tenant_created', message: 'New tenant "ABC Accounting" signed up', time: '2 hours ago' },
    { type: 'payment_received', message: 'Payment of $199.00 received from XYZ Partners', time: '4 hours ago' },
    { type: 'lead_converted', message: 'Lead "Smith & Associates" converted to customer', time: '6 hours ago' },
    { type: 'ticket_opened', message: 'Support ticket #1234 opened by client', time: '8 hours ago' },
    { type: 'tenant_created', message: 'New tenant "Elite Tax Services" signed up', time: '12 hours ago' }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-500 mt-1">Welcome to Practice Manager Pro Admin Portal</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Active Tenants"
          value={stats?.tenants?.active || 0}
          change={`+${stats?.tenants?.newThisMonth || 0} this month`}
          changeType="increase"
          icon={Building2}
          color="blue"
          link="/tenants"
        />
        <StatCard
          title="Monthly Revenue (MRR)"
          value={`$${stats?.revenue?.mrr || '0.00'}`}
          icon={DollarSign}
          color="green"
          link="/subscriptions"
        />
        <StatCard
          title="Open Opportunities"
          value={stats?.sales?.openOpportunities || 0}
          change={`$${stats?.sales?.pipelineValue || 0} pipeline`}
          changeType="increase"
          icon={Target}
          color="purple"
          link="/sales/pipeline"
        />
        <StatCard
          title="Support Tickets"
          value={stats?.support?.openTickets || 0}
          icon={Users}
          color="orange"
          link="/support"
        />
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-500">Trialing Tenants</h3>
            <span className="badge-info">{stats?.tenants?.trialing || 0}</span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-500 rounded-full"
              style={{
                width: `${
                  stats?.tenants?.total
                    ? ((stats?.tenants?.trialing || 0) / stats?.tenants?.total) * 100
                    : 0
                }%`
              }}
            />
          </div>
          <p className="text-xs text-gray-500 mt-2">
            {stats?.tenants?.trialing || 0} of {stats?.tenants?.total || 0} total tenants
          </p>
        </div>

        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-500">Annual Revenue (ARR)</h3>
            <BarChart3 className="w-5 h-5 text-gray-400" />
          </div>
          <p className="text-2xl font-bold text-gray-900">${stats?.revenue?.arr || '0.00'}</p>
          <p className="text-xs text-gray-500 mt-2">Based on current MRR</p>
        </div>

        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-500">New Leads This Month</h3>
            <Target className="w-5 h-5 text-gray-400" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{stats?.sales?.newLeadsThisMonth || 0}</p>
          <Link
            to="/sales/leads"
            className="text-xs text-primary-600 hover:text-primary-700 font-medium"
          >
            View all leads
          </Link>
        </div>
      </div>

      {/* Recent Activity & Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <RecentActivity activities={recentActivities} />
        </div>

        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
          <div className="space-y-3">
            <Link
              to="/tenants/new"
              className="block w-full btn-primary text-center"
            >
              Create New Tenant
            </Link>
            <Link
              to="/sales/leads"
              className="block w-full btn-secondary text-center"
            >
              Add New Lead
            </Link>
            <Link
              to="/marketing/campaigns"
              className="block w-full btn-secondary text-center"
            >
              Create Campaign
            </Link>
            <Link
              to="/support"
              className="block w-full btn-secondary text-center"
            >
              View Support Tickets
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
